import { create } from 'zustand';
import type { Canvas as FabricCanvas, Rect } from 'fabric';
import { useStudioStore } from './studioStore';

export type EditorTool =
  | 'select'
  | 'text'
  | 'shape'
  | 'image'
  | 'button'
  | 'line'
  | 'pen'
  | 'pan'
  | 'zoom'
  | 'eyedropper';

export type ShapeType = 'rectangle' | 'circle' | 'line' | 'triangle';

interface CanvasStoreState {
  fabricCanvas: FabricCanvas | null;
  artboardRef: Rect | null;
  setArtboardRef: (rect: Rect | null) => void;

  activeTool: EditorTool;
  activeShapeType: ShapeType;
  pendingShapeType: string;

  showRulers: boolean;
  showLayerPanel: boolean;
  showPropertyPanel: boolean;

  canvasWidth: number;
  canvasHeight: number;

  isDrawingPath: boolean;

  showBackgroundLayer: boolean;
  showOverlayLayer: boolean;

  lastSampledColor: string | null;

  alignTo: 'selection' | 'artboard';

  setFabricCanvas: (canvas: FabricCanvas | null) => void;
  setActiveTool: (tool: EditorTool) => void;
  setActiveShapeType: (shape: ShapeType) => void;
  setPendingShapeType: (type: string) => void;
  toggleRulers: () => void;
  toggleLayerPanel: () => void;
  togglePropertyPanel: () => void;
  setCanvasDimensions: (width: number, height: number) => void;
  setIsDrawingPath: (drawing: boolean) => void;
  toggleBackgroundLayer: () => void;
  toggleOverlayLayer: () => void;
  setLastSampledColor: (color: string | null) => void;
  setAlignTo: (mode: 'selection' | 'artboard') => void;
}

export const useCanvasStore = create<CanvasStoreState>((set, get) => ({
  fabricCanvas: null,
  artboardRef: null,
  setArtboardRef: (rect) => set({ artboardRef: rect }),

  activeTool: 'select',
  activeShapeType: 'rectangle',
  pendingShapeType: 'rectangle',

  showRulers: true,
  showLayerPanel: true,
  showPropertyPanel: true,

  canvasWidth: 1200,
  canvasHeight: 400,

  isDrawingPath: false,

  showBackgroundLayer: true,
  showOverlayLayer: true,

  lastSampledColor: null,

  alignTo: 'selection',

  setFabricCanvas: (canvas) => set({ fabricCanvas: canvas }),

  setActiveTool: (tool) => {
    const { fabricCanvas } = get();
    if (fabricCanvas) {
      const isDrawing = tool !== 'select' && tool !== 'pan' && tool !== 'zoom';

      if (tool === 'select') {
        fabricCanvas.selection = true;
        fabricCanvas.defaultCursor = 'default';
        fabricCanvas.hoverCursor = 'move';
      } else if (tool === 'pan') {
        fabricCanvas.selection = false;
        fabricCanvas.defaultCursor = 'grab';
        fabricCanvas.hoverCursor = 'grab';
      } else {
        fabricCanvas.selection = false;
        fabricCanvas.defaultCursor = 'crosshair';
        fabricCanvas.hoverCursor = 'crosshair';
      }

      fabricCanvas.forEachObject((obj) => {
        if ((obj as any)._csIsArtboard || (obj as any)._csIsGrid || (obj as any)._csIsPreview) return;
        if (isDrawing) {
          obj.selectable = false;
          obj.evented = false;
        } else if (tool === 'select') {
          const elId = (obj as any)._csElementId as string | undefined;
          if (elId) {
            const el = useStudioStore.getState().canvasState.elements.find(e => e.id === elId);
            const locked = el?.locked ?? false;
            const visible = el?.visible ?? true;
            obj.selectable = !locked && visible;
            obj.evented = !locked && visible;
          } else {
            obj.selectable = true;
            obj.evented = true;
          }
        }
      });
      if (isDrawing) {
        fabricCanvas.discardActiveObject();
      }
      fabricCanvas.requestRenderAll();
    }
    set({ activeTool: tool });
  },

  setActiveShapeType: (shape) => set({ activeShapeType: shape }),
  setPendingShapeType: (type) => set({ pendingShapeType: type }),

  toggleRulers: () => set((s) => ({ showRulers: !s.showRulers })),
  toggleLayerPanel: () => set((s) => ({ showLayerPanel: !s.showLayerPanel })),
  togglePropertyPanel: () => set((s) => ({ showPropertyPanel: !s.showPropertyPanel })),

  setCanvasDimensions: (width, height) => {
    set({ canvasWidth: width, canvasHeight: height });
  },

  setIsDrawingPath: (drawing) => set({ isDrawingPath: drawing }),

  toggleBackgroundLayer: () => set((s) => ({ showBackgroundLayer: !s.showBackgroundLayer })),
  toggleOverlayLayer: () => set((s) => ({ showOverlayLayer: !s.showOverlayLayer })),
  setLastSampledColor: (color) => set({ lastSampledColor: color }),
  setAlignTo: (mode) => set({ alignTo: mode }),
}));

// Deferred to a microtask to break the circular-import TDZ:
// studioStore <-> canvasStore statically import each other, and module-scope
// dereference of useStudioStore crashes when studioStore evaluates first.
// Microtasks run after the full synchronous module graph finishes evaluating,
// so both bindings are guaranteed initialized. The subscription is still
// established before first render or any user interaction.
queueMicrotask(() => {
  useStudioStore.subscribe((state, prevState) => {
    const next = state.canvasState.dimensions;
    const prev = prevState.canvasState.dimensions;
    if (next.width === prev.width && next.height === prev.height) return;
    const current = useCanvasStore.getState();
    if (current.canvasWidth === next.width && current.canvasHeight === next.height) return;
    useCanvasStore.setState({ canvasWidth: next.width, canvasHeight: next.height });
  });
});
